<template>
    <div ref="dropdownWrapper" class="dropdown-wrapper">
        <div ref="inputContainer" class="custom-input-container">
            <span class="custom-input-icon">
                <i class="fas fa-search"></i>
            </span>

            <input
v-model="searchQuery" type="text" class="form-control custom-search-input" :placeholder="placeholder"
                aria-label="Buscar enfermedad" @input="onSearchInput" @focus="showDropdown">
            <button
class="custom-btn-clear-search" type="button" :class="{ 'custom-d-none': !searchQuery }"
                @click="clearSearch">&times;</button>
        </div>
        <!-- Mostrar la lista de historial filtrado en un menú desplegable -->
        <div v-if="showHistory" class="dropdown" @click.stop>
            <ul class="autocomplete-list">
                <li v-for="item in filteredHistory" :key="item" @click="selectHistoryItem(item)">{{ item }}</li>
                <li v-if="filteredHistory.length === 0" class="not-found">¡No se encontraron resultados en el historial
                    de búsqueda!</li>
            </ul>
            <button class="custom-btn-clear-history" type="button" @click="clearHistory">Limpiar el Historial de
                Búsqueda</button>
        </div>
    </div>
</template>

<script setup>
import { ref, defineEmits, onMounted, computed, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        required: true
    },
    placeholder: {
        type: String,
        default: 'Buscar...'
    }
});

const emit = defineEmits(['update:modelValue', 'clear-search']);
const searchQuery = ref(props.modelValue);
const searchHistory = ref([]);
const showHistory = ref(false);
const dropdownWrapper = ref(null);
let typingTimer;
const typingInterval = 500; // Intervalo de tiempo reducido para detectar que el usuario ha dejado de escribir

// Obtener la fecha actual en formato "YYYY-MM-DD"
const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0]; // Solo la parte de la fecha (YYYY-MM-DD)
};

// Función para verificar si es necesario limpiar el historial
const checkAndClearHistory = () => {
    const lastClearDate = localStorage.getItem('lastClearDate');
    const currentDate = getCurrentDate();

    if (lastClearDate !== currentDate) {
        // Si no se ha limpiado hoy, limpiar el historial y actualizar la fecha
        clearHistory();
        localStorage.setItem('lastClearDate', currentDate);
    }
};

// Función debounce para limitar la frecuencia de ejecución de una función
const debounce = (callback, delay) => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(callback, delay);
};

// Función que se ejecuta al cambiar el valor de búsqueda
const onSearchInput = () => {
    debounce(() => {
        emit('update:modelValue', searchQuery.value);
        if (searchQuery.value.trim().length > 1) {
            updateHistory(searchQuery.value);
        }
        showDropdown();
    }, typingInterval);
};

// Mostrar el menú desplegable
const showDropdown = () => {
    showHistory.value = true;
};

// Ocultar el menú desplegable
const hideDropdown = () => {
    showHistory.value = false;
};

// Validar si una palabra es válida
const isValidWord = (word) => {
    const invalidPatterns = [/^[a-zA-Z]$/, /^(.)\1+$/];
    return word.length > 1 && !invalidPatterns.some(pattern => pattern.test(word));
};

// Limitar el historial a 10 entradas
const maxHistorySize = 500;

// Actualizar el historial evitando duplicados y palabras no válidas
const updateHistory = (newValue) => {
    const trimmedValue = newValue.trim();
    if (isValidWord(trimmedValue) && !searchHistory.value.includes(trimmedValue)) {
        searchHistory.value.unshift(trimmedValue);
        if (searchHistory.value.length > maxHistorySize) {
            searchHistory.value.pop(); // Limitar el historial a un tamaño máximo
        }
        saveHistoryToLocalStorage();
    }
};

// Guardar el historial en localStorage
const saveHistoryToLocalStorage = () => {
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory.value));
};

// Limpiar la búsqueda actual y emitir evento al padre
const clearSearch = () => {
    searchQuery.value = '';
    emit('clear-search');
};

// Limpiar todo el historial
const clearHistory = () => {
    searchHistory.value = [];
    localStorage.removeItem('searchHistory');
};

// Seleccionar un ítem del historial
const selectHistoryItem = (item) => {
    searchQuery.value = item;
    showHistory.value = false;
    emit('update:modelValue', item); // Emitimos el valor seleccionado
};

// Filtrar el historial basado en la búsqueda actual
const filteredHistory = computed(() => {
    return searchHistory.value.filter(item => item.toLowerCase().includes(searchQuery.value.toLowerCase()));
});

// Cargar el historial almacenado al montar el componente
onMounted(() => {
    checkAndClearHistory(); // Verificar si es necesario limpiar el historial

    const storedHistory = localStorage.getItem('searchHistory');
    if (storedHistory) {
        try {
            const parsedHistory = JSON.parse(storedHistory);
            if (Array.isArray(parsedHistory)) {
                searchHistory.value = parsedHistory.filter(item => typeof item === 'string');
            }
        } catch (e) {
            console.error('Error al parsear el historial desde localStorage', e);
        }
    }
});

// Ocultar el menú desplegable al hacer clic fuera del componente
document.addEventListener('click', (event) => {
    if (dropdownWrapper.value && !dropdownWrapper.value.contains(event.target)) {
        hideDropdown();
    }
});

// Guardar el historial en localStorage cuando cambie
watch(searchHistory, () => {
    saveHistoryToLocalStorage();
}, { deep: true });

// Escuchar cambios en `modelValue` para mantener el input sincronizado
watch(() => props.modelValue, (newVal) => {
    searchQuery.value = newVal;
});
</script>


<style scoped>
.custom-input-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
}

.custom-search-input {
    padding-left: 30px;
    width: 100%;
    flex: 1;
    box-sizing: border-box;
}

.custom-search-input:focus {
    border-radius: 6px 6px 0px 0px;
}

.custom-btn-clear-search {
    background: none;
    border: none;
    cursor: pointer;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.2em;
    color: #aaa;
}

.custom-d-none {
    display: none;
}

.custom-input-container {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    position: relative;
    width: 100%;
    z-index: 100;
    max-width: 100%;
}

.dropdown-wrapper {
    position: relative;
    width: 100%;
}

.dropdown {
    position: absolute;
    display: block !important;
    opacity: 1;
    visibility: visible;
    background: white;
    border: 1px solid #ccc;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 101;
    top: 40px;
    left: 0;
    width: 100%;
}

.autocomplete-list {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 200px;
    overflow-y: auto;
    width: 100%;
}

.autocomplete-list li {
    padding: 10px;
    cursor: pointer;
}

.autocomplete-list li:hover {
    background: #eee;
}

.not-found {
    cursor: default;
    padding: 10px;
    color: #aaa;
}

.custom-btn-clear-history {
    background: none;
    border: none;
    cursor: pointer;
    width: 100%;
    padding: 10px;
    text-align: center;
    font-size: 1em;
    color: #aaa;
    border-top: 1px solid #ccc;
}
</style>
